import * as React from 'react';
import { t, type MessageKey } from '@reos/i18n';
import { useQuery } from '@tanstack/react-query';
import {
  fetchRegistry,
  previewBulkAction,
  runBulkAction,
  type BulkAction,
  type BulkDealState,
  type BulkPlan,
  type BulkRunResult,
} from '../lib/connectors.js';
import { listMembers } from '../lib/members-api.js';

/**
 * Krok SR1 bod 4 — HROMADNE AKCE nad vybranymi nemovitostmi v seznamu.
 *
 * Pravidlo kroku: NAHLED DOPADU PRED POTVRZENIM. Tlacitko „Zobrazit dopad"
 * se zepta serveru (`/v1/publications/bulk/preview`), co by se stalo — vcetne
 * duvodu, proc se ktery radek preskoci. Potvrdit jde teprve pak.
 */

const ACTIONS: readonly BulkAction[] = ['EXPORT', 'WITHDRAW', 'DEAL_STATE', 'ASSIGN_BROKER', 'ARCHIVE'];
const DEAL_STATES: readonly BulkDealState[] = ['ACTIVE', 'RESERVED', 'SOLD'];

export function BulkPortalActions({
  propertyIds,
  onDone,
}: {
  readonly propertyIds: readonly string[];
  readonly onDone: () => void;
}): React.JSX.Element {
  const [action, setAction] = React.useState<BulkAction>('EXPORT');
  const [portals, setPortals] = React.useState<readonly string[]>([]);
  const [dealState, setDealState] = React.useState<BulkDealState>('RESERVED');
  const [brokerId, setBrokerId] = React.useState('');
  const [plan, setPlan] = React.useState<BulkPlan | null>(null);
  const [result, setResult] = React.useState<BulkRunResult | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState(false);

  const registry = useQuery({ queryKey: ['connector-registry'], queryFn: fetchRegistry });
  const members = useQuery({ queryKey: ['members'], queryFn: listMembers });
  const connectable = (registry.data ?? []).filter((portal) => portal.connectorImplemented);

  const body = {
    action,
    propertyIds,
    ...(portals.length > 0 ? { portals } : {}),
    ...(action === 'DEAL_STATE' ? { dealState } : {}),
    ...(action === 'ASSIGN_BROKER' && brokerId !== '' ? { brokerId } : {}),
  };

  const run = (fn: () => Promise<unknown>, after: (value: unknown) => void): void => {
    setBusy(true);
    setError(null);
    void fn()
      .then(after)
      .catch((cause: unknown) => setError(cause instanceof Error ? cause.message : String(cause)))
      .finally(() => setBusy(false));
  };

  const togglePortal = (portal: string): void => {
    setPlan(null);
    setPortals((prev) => (prev.includes(portal) ? prev.filter((id) => id !== portal) : [...prev, portal]));
  };

  const needsPortals = action === 'EXPORT' || action === 'WITHDRAW';

  return (
    <section className="rh-card p-4 mb-4" data-testid="bulk-portal-actions">
      <h2 className="font-semibold mb-2">
        {t('webAdmin.portaly.bulk.heading', { locale: 'cs', values: { count: String(propertyIds.length) } })}
      </h2>

      <div className="flex flex-wrap items-end gap-3">
        <label className="text-sm">
          {t('webAdmin.portaly.bulk.actionLabel', { locale: 'cs' })}
          <select
            className="border rounded px-2 py-1 text-sm block"
            value={action}
            onChange={(event) => {
              setAction(event.target.value as BulkAction);
              setPlan(null);
              setResult(null);
            }}
            data-testid="bulk-action"
          >
            {ACTIONS.map((value) => (
              <option key={value} value={value}>
                {t(`webAdmin.portaly.bulk.action.${value}`, { locale: 'cs' })}
              </option>
            ))}
          </select>
        </label>

        {action === 'DEAL_STATE' ? (
          <label className="text-sm">
            {t('webAdmin.portaly.bulk.action.DEAL_STATE', { locale: 'cs' })}
            <select
              className="border rounded px-2 py-1 text-sm block"
              value={dealState}
              onChange={(event) => {
                setDealState(event.target.value as BulkDealState);
                setPlan(null);
              }}
              data-testid="bulk-deal-state"
            >
              {DEAL_STATES.map((value) => (
                <option key={value} value={value}>
                  {t(`webAdmin.portaly.bulk.dealState.${value}`, { locale: 'cs' })}
                </option>
              ))}
            </select>
          </label>
        ) : null}

        {action === 'ASSIGN_BROKER' ? (
          <label className="text-sm">
            {t('webAdmin.portaly.bulk.brokerLabel', { locale: 'cs' })}
            <select
              className="border rounded px-2 py-1 text-sm block"
              value={brokerId}
              onChange={(event) => {
                setBrokerId(event.target.value);
                setPlan(null);
              }}
              data-testid="bulk-broker"
            >
              <option value="" />
              {(members.data ?? []).map((member) => (
                <option key={member.userId} value={member.userId}>
                  {member.name}
                </option>
              ))}
            </select>
          </label>
        ) : null}
      </div>

      {needsPortals ? (
        <fieldset className="mt-3">
          <legend className="text-sm font-medium">
            {t('webAdmin.portaly.bulk.portalsLabel', { locale: 'cs' })}
          </legend>
          <p className="text-sm" style={{ color: 'var(--rh-text-muted)' }}>
            {portals.length === 0 ? t('webAdmin.portaly.bulk.portalsAll', { locale: 'cs' }) : ''}
          </p>
          <div className="flex flex-wrap gap-3 mt-1">
            {connectable.map((portal) => (
              <label key={portal.portal} className="text-sm flex items-center gap-1">
                <input
                  type="checkbox"
                  checked={portals.includes(portal.portal)}
                  onChange={() => togglePortal(portal.portal)}
                  data-testid={`bulk-portal-${portal.portal}`}
                />
                {portal.name}
              </label>
            ))}
          </div>
        </fieldset>
      ) : null}

      <div className="flex flex-wrap gap-2 mt-3">
        <button
          type="button"
          className="rh-btn"
          disabled={busy}
          onClick={() => run(() => previewBulkAction(body), (value) => { setPlan(value as BulkPlan); setResult(null); })}
          data-testid="bulk-preview"
        >
          {t('webAdmin.portaly.bulk.previewButton', { locale: 'cs' })}
        </button>
        <button
          type="button"
          className="rh-btn rh-btn--primary"
          disabled={busy || plan === null || plan.portalCalls === 0}
          onClick={() =>
            run(
              () => runBulkAction(body),
              (value) => {
                setResult(value as BulkRunResult);
                setPlan(null);
                onDone();
              },
            )
          }
          data-testid="bulk-confirm"
        >
          {t('webAdmin.portaly.bulk.confirmButton', { locale: 'cs' })}
        </button>
      </div>

      {error !== null ? (
        <p className="text-sm mt-2" role="alert" style={{ color: 'var(--rh-danger)' }}>
          {t('webAdmin.portaly.bulk.error', { locale: 'cs' })} {error}
        </p>
      ) : null}

      {plan !== null ? (
        <div className="mt-3" data-testid="bulk-plan">
          <p className="text-sm font-medium">
            {t('webAdmin.portaly.bulk.previewSummary', {
              locale: 'cs',
              values: { listings: String(plan.affectedListings), calls: String(plan.portalCalls) },
            })}
          </p>
          {plan.portalCalls === 0 ? (
            <p className="text-sm">{t('webAdmin.portaly.bulk.previewNothing', { locale: 'cs' })}</p>
          ) : null}
          <ul className="text-sm list-disc pl-5">
            {plan.items.map((item) => (
              <li key={item.propertyId}>
                <span className="font-medium">{item.title ?? item.propertyId.slice(0, 8)}</span>{' '}
                {item.portals.length > 0 ? item.portals.join(', ') : ''}
                {item.skipped.map((skip) => (
                  <span key={`${String(skip.portal)}:${skip.reason}`} style={{ color: 'var(--rh-text-muted)' }}>
                    {' '}
                    {skip.portal === null ? '' : `${skip.portal}: `}
                    {t(`webAdmin.portaly.bulk.skipReason.${skip.reason}` as MessageKey, { locale: 'cs' })}
                  </span>
                ))}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {result !== null ? (
        <div className="mt-3" data-testid="bulk-result">
          <p className="text-sm font-medium">
            {t('webAdmin.portaly.bulk.resultSummary', {
              locale: 'cs',
              values: { queued: String(result.queued), calls: String(result.portalCalls) },
            })}
          </p>
          {result.approvalRequests.length > 0 ? (
            <p className="text-sm">
              {t('webAdmin.portaly.bulk.resultApprovals', {
                locale: 'cs',
                values: { count: String(result.approvalRequests.length) },
              })}
            </p>
          ) : null}
          {result.failures.length > 0 ? (
            <p className="text-sm" style={{ color: 'var(--rh-danger)' }}>
              {t('webAdmin.portaly.bulk.resultFailures', {
                locale: 'cs',
                values: { count: String(result.failures.length) },
              })}
            </p>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}
